import { css } from '@emotion/core'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'

import { breakpoints } from '../constants/breakPoints'
// Icons
import AngelCoIcon from '../icons/social/angel-co-logo.svg'
import LinkedinIcon from '../icons/social/logo-linkedin.svg'
import XIcon from '../icons/social/logo-x.svg'
import YoutubeIcon from '../icons/social/logo-youtube.svg'
import MediumIcon from '../icons/social/medium-logo.svg'
import WebsiteIcon from '../icons/social/website-logo.svg'
import { Link } from './Link'

export interface AvatarIntroProps {
  blackBG?: boolean
  withoutMarginBottom?: boolean
  name: string
  companyName?: string
  jobTitle?: string

  authorPhoto: {
    gatsbyImageData: any
  }

  description?: string

  socialLinks?: {
    title: string
    url: string
  }[]
  children?: React.ReactNode
}

const AvatarIntro: React.FC<AvatarIntroProps> = ({
  blackBG = false,
  withoutMarginBottom = false,
  name,
  companyName,
  jobTitle,
  authorPhoto,
  description,
  socialLinks = [],
  children,
}) => {
  return (
    <div css={containerStyle(blackBG, withoutMarginBottom)} className="full-screen-relative">
      <GatsbyImage
        image={authorPhoto.gatsbyImageData}
        css={imageStyle}
        imgStyle={{ width: '8rem', height: '8rem' }}
        alt={name}
        loading="eager"
      />

      <h1 className="heading2 center">{name}</h1>
      <div className="subtitle center">
        {jobTitle} {companyName && '@'} {companyName}
      </div>
      <div className="subtitle center">{description}</div>
      {socialLinks && (
        <div css={socialSharingStyle}>
          {socialLinks.map(({ title, url }) => {
            const lowercaseTitle = title.toLowerCase()

            switch (true) {
              case lowercaseTitle.includes('angel.co'):
                return (
                  <Link className="opacity-on-hover" href={url} key={url}>
                    <AngelCoIcon className={blackBG ? 'white-icon' : undefined} css={socialIconStyle} />
                  </Link>
                )
              case lowercaseTitle.includes('linkedin'):
                return (
                  <Link className="opacity-on-hover" href={url} key={url}>
                    <LinkedinIcon className={blackBG ? 'white-icon' : undefined} css={socialIconStyle} />
                  </Link>
                )
              case lowercaseTitle.includes('medium'):
                return (
                  <Link className="opacity-on-hover" href={url} key={url}>
                    <MediumIcon className={blackBG ? 'white-icon' : undefined} css={socialIconStyle} />
                  </Link>
                )
              case lowercaseTitle.includes('twitter'):
                return (
                  <Link className="opacity-on-hover" href={url} key={url}>
                    <XIcon className={blackBG ? 'white-icon' : undefined} css={socialIconStyle} />
                  </Link>
                )
              case lowercaseTitle.includes('youtube'):
                return (
                  <Link className="opacity-on-hover" href={url} key={url}>
                    <YoutubeIcon className={blackBG ? 'white-icon' : undefined} css={socialIconStyle} />
                  </Link>
                )
              case lowercaseTitle.includes('website'):
                return (
                  <Link className="opacity-on-hover" href={url} key={url}>
                    <WebsiteIcon className={blackBG ? 'white-icon' : undefined} css={socialIconStyle} />
                  </Link>
                )

              default:
                return null
            }
          })}
        </div>
      )}
      {children}
    </div>
  )
}

export default AvatarIntro

const containerStyle = (blackBG: boolean, withoutMarginBottom: boolean) => css`
  position: relative;
  padding: 4rem 0 ${withoutMarginBottom && '8rem'};
  margin: 0 auto ${!withoutMarginBottom ? '6rem' : 0};
  background-image: url('/images/hero${blackBG && '-dark'}.webp');
  background-repeat: no-repeat;
  backdrop-filter: linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  background-size: cover;
  background-position: center;
  border-bottom: 1px solid rgba(136, 146, 158, 0.4);

  text-align: center;

  .heading2 {
    margin: 2rem auto 0.5rem;
    text-align: center !important;
  }

  .subtitle {
    margin: 0 auto 2rem;
    max-width: 35rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding: 3rem 0 ${withoutMarginBottom && '7rem'};
  }
`

const imageStyle = css`
  margin: 0 auto 2rem auto;
  border-radius: 50%;
  width: 8rem;
  height: 8rem;
`

const socialSharingStyle = css`
  display: grid;
  margin: 0 auto;
  grid-gap: 1.5rem;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  justify-content: center;
  align-items: center;
  width: fit-content;

  a {
    display: flex;
  }
`

const socialIconStyle = css`
  width: 1.5rem;
  height: auto;
`
